import { LoadingOutlined } from '@ant-design/icons';
import { Dropdown, Spin } from 'antd';
import React, { useState } from 'react';
import Hotkeys from 'react-hot-keys';
import QRCode from 'react-qr-code';
import styled, { css } from 'styled-components';

import ArrowRightSvg from '../../../assets/icons/ArrowRightSvg';
import QuickPlaySvg from '../../../assets/icons/QuickPlaySvg';
import theme from '../../../assets/theme';
import { Switch } from '../_Switch';

export const DropdownButtonItem = ({
  icon,
  title,
  description,
  includeSwitch,
  onSwitchToggled,
  switchValue,
  qrLink,
  isDarkTheme,
}: DropdownButtonItemProps) => {
  return (
    <MenuItem
      $isDarkTheme={!!isDarkTheme}
      onClick={() => onSwitchToggled && onSwitchToggled(!switchValue)}
    >
      <Left $isDarkTheme={!!isDarkTheme}>{icon}</Left>
      <Right $isDarkTheme={!!isDarkTheme}>
        <div
          style={{
            position: 'relative',
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <p
            style={{
              margin: 0,
              fontWeight: 'bold',
              fontSize: 18,
              color: isDarkTheme ? 'white' : theme.colors.translucentLightBlack,
            }}
          >
            {title}
          </p>
          {includeSwitch && (
            <Switch
              style={{ marginLeft: 10 }}
              color={theme.colors.primary}
              value={switchValue}
              onChange={(bool, e) => {
                e.stopPropagation();
                onSwitchToggled && onSwitchToggled(bool);
              }}
            />
          )}
        </div>
        <p
          style={{
            marginTop: isDarkTheme ? 2 : 4,
            marginBottom: 2,
            fontSize: isDarkTheme ? 10 : 14,
            lineHeight: isDarkTheme ? 1.3 : 1.5,
            fontWeight: 'bold',
            color: isDarkTheme ? 'white' : theme.colors.translucentLightBlack,
          }}
        >
          {description}
        </p>
        {qrLink && (
          <div
            style={{
              padding: '10px',
              backgroundColor: 'white',
              width: '120px',
              height: '120px',
              borderRadius: '10px',
            }}
          >
            <QRCode value={qrLink} size={100} />
          </div>
        )}
      </Right>
    </MenuItem>
  );
};

export type DropdownButtonItemProps = {
  icon: React.ReactNode;
  title: string;
  description?: string;
  includeSwitch?: boolean;
  switchValue?: boolean;
  onSwitchToggled?: (bool: boolean) => void;
  qrLink?: string;
  isDarkTheme: boolean;
};

const DropDownButton = ({
  onClick,
  children,
  menu,
  isDisabled = false,
  isLoading,
  style,
  isPresentButton,
}: {
  menu: React.ReactElement;
  onClick?: () => void;
  children?: React.ReactNode;
  isDisabled?: boolean;
  isLoading?: boolean;
  isPresentButton?: boolean;
  style?: React.CSSProperties;
}) => {
  const [showDropdown, setShowDropdown] = useState(false);

  const handleEscapePress = () => {
    setShowDropdown(false);
  };

  return (
    <StyledDropdownButton $isDisabled={isDisabled} style={style}>
      <LeftContainer
        onClick={() => onClick && !isDisabled && onClick()}
        $isPresentButton={isPresentButton}
      >
        {isLoading ? (
          <Spin
            indicator={<LoadingOutlined style={{ fontSize: 20, color: 'white' }} spin />}
          />
        ) : (
          <QuickPlaySvg />
        )}
        {children && <span>{children}</span>}
      </LeftContainer>
      <>
        <Dropdown
          placement="bottomRight"
          visible={showDropdown && !isDisabled}
          onVisibleChange={setShowDropdown}
          trigger={['click']}
          overlay={menu}
        >
          <DropdownButton $isPresentButton={isPresentButton}>
            <PaddedBorder
              style={{ marginRight: '10px' }}
              $isPresentButton={isPresentButton}
            />
            <ArrowRightSvg />
          </DropdownButton>
        </Dropdown>
      </>
      <Hotkeys keyName="Escape" onKeyUp={handleEscapePress} />
    </StyledDropdownButton>
  );
};

const Right = styled.div<{ $isDarkTheme: boolean }>`
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  padding-left: 10px;
`;

const Left = styled.div<{ $isDarkTheme: boolean }>`
  flex: 0;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  svg {
    height: 22px;
    width: 22px;
    ${({ $isDarkTheme }) =>
      $isDarkTheme &&
      css`
        color: ${theme.colors.white};
      `}
  }
`;

const MenuItem = styled.div<{ $isDarkTheme: boolean }>`
  display: flex;
  max-width: 250px;
  ${({ $isDarkTheme }) =>
    $isDarkTheme
      ? css`
          padding: 2px 0px;
        `
      : css`
          padding: 10px 0px;
        `}
  p {
    white-space: normal;
  }
`;

const PaddedBorder = styled.div<{ $isPresentButton?: boolean }>`
  width: 1px;
  background-color: ${({ $isPresentButton }) =>
    $isPresentButton ? theme.colors.black : 'rgba(255, 255, 255, 0.5);'};
  height: 36px;
  border-radius: 50px;
`;

const ButtonStyles = styled.div<{ $isDisabled?: boolean; $isPresentButton?: boolean }>`
  transition: all 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);
  &:hover {
    background-color: ${({ $isDisabled, $isPresentButton }) =>
      $isDisabled ? 'gray' : $isPresentButton ? theme.colors.white : '#14857a'};
  }
  &:focus {
    background-color: ${({ $isDisabled, $isPresentButton }) =>
      $isDisabled ? 'gray' : $isPresentButton ? theme.colors.white : '#14857a'};
  }
  &:active {
    background-color: ${({ $isDisabled, $isPresentButton }) =>
      $isDisabled ? 'gray' : $isPresentButton ? theme.colors.white : '#a2b3b1'};
  }
`;

const DropdownButton = styled(ButtonStyles)`
  display: flex;
  align-items: center;
  height: 100%;
  padding-right: 10px;
  && svg {
    transform: rotate(90deg);
    height: 22px;
    width: 22px;
  }
  border-top-right-radius: 5px;
  border-bottom-right-radius: 5px;
`;

const LeftContainer = styled(ButtonStyles)`
  display: flex;
  align-items: center;
  padding-left: 10px;
  padding-right: 10px;
  width: 100%;
  flex: 1;
  height: 100%;
  & > *:not(:first-child) {
    margin-left: 10px;
  }

  border-top-left-radius: 5px;
  border-bottom-left-radius: 5px;
`;

const StyledDropdownButton = styled.button<{
  $isDisabled?: boolean;
}>`
  background-color: ${({ $isDisabled }) => ($isDisabled ? 'gray' : theme.colors.primary)};
  color: ${theme.colors.white};
  opacity: ${({ $isDisabled }) => ($isDisabled ? 0.7 : 1)};
  cursor: ${({ $isDisabled }) => ($isDisabled ? 'not-allowed' : 'pointer')};
  font-weight: bold;
  font-size: 16px;
  display: flex;
  font-family: 'Nunito';
  font-style: normal;
  line-height: 22px;
  width: fit-content;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: space-between;

  outline: none;
  border: none;
  padding: 0;
  border-radius: 5px;
`;

export default DropDownButton;
